import React, { useMemo } from "react";
import { styled } from "@mui/material/styles";
import { Autocomplete } from "components/Autocomplete";
import { useSitesQuery } from "hooks/queries/useSitesQuery";
import { Site } from "interfaces/site";
import {
  CaseStatusCategory,
  CaseStatusTypes
} from "interfaces/case-status-type";
import { useGetCaseStatusTypes } from "hooks/case-status-types/useCaseStatusTypes";

export interface ActiveIssueFiltersProps {
  filters: { siteIds: number[]; statusTypeNames: string[] };
  setFilters: (filters: {
    siteIds: number[];
    statusTypeNames: string[];
  }) => void;
}

export const ActiveIssueFilters = ({
  filters,
  setFilters
}: ActiveIssueFiltersProps): JSX.Element => {
  const {
    isLoading: sitesLoading,
    isError: sitesError,
    data: _sites
  } = useSitesQuery();
  const {
    loading: caseStatusTypeLoading,
    error: caseStatusTypeError,
    caseStatusTypes
  } = useGetCaseStatusTypes();

  // ensure variable is defined for typescript
  const sites = useMemo(() => _sites?.results ?? [], [_sites]);

  const filteredCaseStatusTypes = useMemo(() => {
    let statusTypes: CaseStatusTypes[] = [];
    if (caseStatusTypes) {
      statusTypes = caseStatusTypes.filter(
        (el: CaseStatusTypes) => el.category !== CaseStatusCategory.COMPLETE
      );
    }
    return statusTypes;
  }, [caseStatusTypes]);

  return (
    <StyledPaper>
      <WidthConstrainedAutocomplete
        loading={sitesLoading}
        label="Site"
        placeholder=""
        size="small"
        multiple
        limitTags={1}
        autoHighlight
        disableCloseOnSelect
        autoComplete
        getOptionLabel={(option: Site) => option.name}
        options={sites}
        error={sitesError}
        onChange={(e: React.ChangeEvent, selectedSites: Site[]) =>
          setFilters({
            ...filters,
            siteIds: selectedSites.map(site => site.id)
          })
        }
      />
      <WidthConstrainedAutocomplete
        loading={caseStatusTypeLoading}
        label="Case Status"
        placeholder=""
        size="small"
        multiple
        limitTags={1}
        autoHighlight
        disableCloseOnSelect
        autoComplete
        getOptionLabel={(option: CaseStatusTypes) => option.name}
        isOptionEqualToValue={(
          option: CaseStatusTypes,
          value: CaseStatusTypes
        ) => option.name === value.name}
        options={filteredCaseStatusTypes}
        error={caseStatusTypeError}
        onChange={(e: React.ChangeEvent, statuses: CaseStatusTypes[]) =>
          setFilters({
            ...filters,
            statusTypeNames: statuses.map((x: CaseStatusTypes) => x.name)
          })
        }
      />
    </StyledPaper>
  );
};

const StyledPaper = styled("div")`
  padding: ${props => props.theme.spacing(2)} 0 0;
  display: flex;
  width: 100%;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: wrap;
  @media (max-width: 960px) {
    justify-content: center;
  }
`;

const WidthConstrainedAutocomplete = styled(Autocomplete)`
  min-width: 200px;
  max-width: 400px;
`;
